import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from './Toast';
import { CAT, Avatar, Dots, Progress, fmtTime } from './ShareResults';
import './GamePlayUI.css';

const shuffle = (a) => {
  const b = [...a];
  for (let i = b.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [b[i], b[j]] = [b[j], b[i]];
  }
  return b;
};
const sameSet = (a, b) => a.length === b.length && a.every((w) => b.includes(w));

function Clock({ startedAt, endedAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (endedAt || !startedAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [startedAt, endedAt]);
  const seconds = startedAt ? ((endedAt || now) - startedAt) / 1000 : 0;
  return <div className="race-clock" aria-label="Time">{fmtTime(seconds)}</div>;
}

function PlayersPanel({ players }) {
  const order = useMemo(() => [...players].sort((a, b) => {
    const at = a.status === 'completed' ? a.timeTaken : null;
    const bt = b.status === 'completed' ? b.timeTaken : null;
    if (at != null || bt != null) return (at ?? Infinity) - (bt ?? Infinity);
    return b.foundLevels.length - a.foundLevels.length;
  }), [players]);
  return (
    <aside className="cr-card race-side" aria-label="Players">
      <div className="cr-row-head race-side-head">
        <h2 className="cr-section-title">Players</h2>
        <span className="cr-label">Groups found</span>
      </div>
      <div>
        {order.map((p) => {
          let meta;
          if (p.status === 'completed') meta = <b className="race-side-time">{fmtTime(p.timeTaken)}</b>;
          else if (p.forfeited) meta = 'Left';
          else if (p.status === 'out') meta = 'Out of lives';
          else if (!p.connected) meta = 'Offline';
          else meta = `${p.foundLevels.length}/4`;
          return (
            <div key={p.playerId} className={`cr-tr race-side-row${p.isYou ? ' cr-me' : ''}`}>
              <div className="race-side-who">
                <span className="cr-player">
                  <Avatar name={p.username} color={p.color} />
                  <b>{p.isYou ? 'You' : p.username}</b>
                </span>
                <Progress levels={p.foundLevels} />
              </div>
              <div className="race-side-meta cr-tnum">
                <div>{meta}</div>
                <Dots left={p.lives} small />
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
}

// The board, shared by multiplayer races and the daily challenge.
//
// onSubmit(words) must resolve to one of
//   { type: 'correct', foundCount }            (the parent adds the group to foundGroups)
//   { type: 'incorrect', oneAway, lives }
//   { type: 'rejected', message }
//   { type: 'error', message }
const GamePlayUI = ({
  label,
  words,
  foundGroups,
  guesses = [],
  lives,
  startedAt,
  endedAt = null,
  locked = false,
  onSubmit,
  players = null,
  footer = null,
}) => {
  const [order, setOrder] = useState(words);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);
  const [lift, setLift] = useState(false);
  const wordsKey = words.join('|');

  useEffect(() => {
    setOrder(wordsKey ? wordsKey.split('|') : []);
    setSelected([]);
  }, [wordsKey]);

  const foundWords = useMemo(() => foundGroups.flatMap((g) => g.words), [foundGroups]);
  const remaining = order.filter((w) => !foundWords.includes(w));
  const canPlay = !locked && lives > 0 && foundGroups.length < 4;

  useEffect(() => {
    setSelected((sel) => (sel.some((w) => foundWords.includes(w)) ? sel.filter((w) => !foundWords.includes(w)) : sel));
  }, [foundWords]);

  const toggle = (w) => {
    if (!canPlay || busy) return;
    setSelected((sel) => (sel.includes(w) ? sel.filter((x) => x !== w) : sel.length < 4 ? [...sel, w] : sel));
  };

  const submit = useCallback(async () => {
    if (!canPlay || busy || selected.length !== 4) return;
    if (guesses.some((g) => sameSet(g, selected))) {
      toast('Already guessed!');
      return;
    }
    setBusy(true);
    setLift(true);
    const [res] = await Promise.all([onSubmit(selected), new Promise((r) => setTimeout(r, 380))]);
    setLift(false);
    setBusy(false);
    if (!res) return;
    if (res.type === 'correct') {
      setSelected([]);
      if (res.foundCount >= 4) toast('Puzzle Solved!');
    } else if (res.type === 'incorrect') {
      setShake(false);
      requestAnimationFrame(() => setShake(true));
      if (res.lives === 0) toast('Out of Lives');
      else toast(res.oneAway ? 'One away!' : 'Not quite');
    } else if (res.message) {
      toast(res.message, { error: res.type === 'error' });
    }
  }, [canPlay, busy, selected, guesses, onSubmit]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Enter' || e.repeat) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (t && t.tagName === 'BUTTON' && !t.classList.contains('cr-tile')) return;
      e.preventDefault();
      submit();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [submit]);

  const shuffleBoard = () => {
    const rest = shuffle(remaining);
    setOrder([...order.filter((w) => foundWords.includes(w)), ...rest]);
  };

  return (
    <div className="cr-page">
      <div className={`race${players ? '' : ' race-solo'}`}>
        <section className="race-board" aria-label="Board">
          <div className="race-head">
            <div className="cr-label">{label}</div>
            <Clock startedAt={startedAt} endedAt={endedAt} />
          </div>
          <p className="race-hint">Select 4 words that you think belong together</p>
          <div className="race-grid">
            {foundGroups.map((g) => (
              <div key={g.level} className="cr-solved" style={{ background: CAT[g.level] }}>
                <b>{g.categoryName}</b>
                <span>{g.words.join(', ')}</span>
              </div>
            ))}
            {remaining.length > 0 && (
              <div
                className={`cr-tiles${shake ? ' shake' : ''}`}
                onAnimationEnd={(e) => { if (e.target === e.currentTarget) setShake(false); }}
              >
                {remaining.map((w) => {
                  const idx = selected.indexOf(w);
                  return (
                    <button
                      key={w}
                      type="button"
                      className={`cr-tile${w.length > 7 ? ' long' : ''}${lift && idx !== -1 ? ' lift' : ''}`}
                      style={lift && idx !== -1 ? { animationDelay: `${idx * 60}ms` } : undefined}
                      aria-pressed={idx !== -1}
                      disabled={!canPlay}
                      onClick={() => toggle(w)}
                    >
                      {w}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="race-mistakes">
            <span>Mistakes remaining:</span>
            <Dots left={lives} />
          </div>
          {footer || (
            <div className="race-controls">
              <button type="button" className="cr-btn" onClick={shuffleBoard} disabled={!canPlay}>Shuffle</button>
              <button type="button" className="cr-btn" onClick={() => setSelected([])} disabled={!canPlay || selected.length === 0}>
                Deselect All
              </button>
              <button type="button" className="cr-btn solid" onClick={submit} disabled={!canPlay || busy || selected.length !== 4}>
                Submit
              </button>
            </div>
          )}
        </section>
        {players && <PlayersPanel players={players} />}
      </div>
    </div>
  );
};

export default GamePlayUI;
